<template lang="pug">
section.cte-detail(v-loading="loading")
  template(v-if="cte")
    el-header
      el-page-header(@back="goBack" :content="`CT-e ${cte.number || ''} / Série ${cte.series || ''}`")

    el-tag(v-if="cte.cancelledAt" type="danger" size="medium").cancelled-tag
      | Cancelado em {{ formatDate(cte.cancelledAt) }}

    h3 Identificação

    el-descriptions(:column="2" border)
      el-descriptions-item(label="Chave de acesso")
        code.access-key {{ cte.accessKey }}
      el-descriptions-item(label="Emitido em") {{ formatDate(cte.issuedAt) }}
      el-descriptions-item(label="Número") {{ cte.number || '—' }}
      el-descriptions-item(label="Série") {{ cte.series || '—' }}
      el-descriptions-item(label="Status SEFAZ") {{ describeStatus(cte.statusCode) }}
      el-descriptions-item(label="Nº do protocolo") {{ cte.protocolNumber || '—' }}
      el-descriptions-item(label="Valor total") {{ formatCurrency(cte.totalValue) }}
      el-descriptions-item(label="Ambiente") {{ cte.environment }}

    h3 Partes

    el-descriptions(:column="2" border)
      el-descriptions-item(label="Emitente")
        div {{ cte.issuerName || '—' }}
        small.cnpj {{ formatCnpj(cte.issuerCnpj) }}
      el-descriptions-item(label="Tomador (CNPJ)")
        small.cnpj {{ formatCnpj(cte.recipientCnpj) }}

    h3 Armazenamento

    el-descriptions(:column="1" border)
      el-descriptions-item(label="NSU") {{ cte.nsu }}
      el-descriptions-item(label="Buscado em") {{ formatDate(cte.fetchedAt) }}
      el-descriptions-item(label="S3")
        code.s3-path s3://{{ cte.s3Bucket }}/{{ cte.s3Key }}

    h3 Eventos
      el-tag.event-count(size="mini") {{ cteEvents.length }}

    el-empty(v-if="!cteEvents.length && !loadingEvents" description="Nenhum evento registrado para este CT-e.")

    el-table(v-else :data="cteEvents" size="mini" v-loading="loadingEvents" style="width: 100%")
      el-table-column(label="Tipo" width="280")
        template(slot-scope="scope")
          div {{ describeEventType(scope.row.eventType) }}
          small.code {{ scope.row.eventType }} • seq {{ scope.row.sequence }}
      el-table-column(prop="occurredAt" label="Ocorrido em" width="160")
        template(slot-scope="scope") {{ formatDate(scope.row.occurredAt) }}
      el-table-column(prop="registeredAt" label="Registrado em" width="160")
        template(slot-scope="scope") {{ formatDate(scope.row.registeredAt) }}
      el-table-column(prop="protocolNumber" label="Protocolo" width="160")
      el-table-column(label="Status")
        template(slot-scope="scope")
          div {{ describeStatus(scope.row.statusCode) }}
          small.reason(v-if="scope.row.reason") {{ scope.row.reason }}

    //- MDF-e events live in their own section. They're operational metadata for the
    //- carrier's manifest (excluded from the accountant's export, see CTeService), so
    //- we render them after the fiscal events and only when present.
    template(v-if="mdfeEvents.length")
      h3 Eventos do MDF-e
        el-tag.event-count(size="mini" type="info") {{ mdfeEvents.length }}

      small.section-hint Eventos do manifesto de transporte. Não incluídos no ZIP de exportação.

      el-table(:data="mdfeEvents" size="mini" v-loading="loadingEvents" style="width: 100%")
        el-table-column(label="Tipo" width="280")
          template(slot-scope="scope")
            div {{ describeEventType(scope.row.eventType) }}
            small.code {{ scope.row.eventType }} • seq {{ scope.row.sequence }}
        el-table-column(prop="occurredAt" label="Ocorrido em" width="160")
          template(slot-scope="scope") {{ formatDate(scope.row.occurredAt) }}
        el-table-column(prop="registeredAt" label="Registrado em" width="160")
          template(slot-scope="scope") {{ formatDate(scope.row.registeredAt) }}
        el-table-column(prop="protocolNumber" label="Protocolo" width="160")
        el-table-column(label="Status")
          template(slot-scope="scope")
            div {{ describeStatus(scope.row.statusCode) }}
            small.reason(v-if="scope.row.reason") {{ scope.row.reason }}

  el-empty(v-else-if="!loading" description="CT-e não encontrado.")
</template>

<script>
import moment from 'moment';

import CteService from '@/services/fulfillment/CteService';

export default {
  name: 'cte-detail',
  props: {
    accessKey: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      loadingEvents: false,
      cte: null,
      events: [],
    };
  },
  computed: {
    // Split the API response into the two tables the template renders. The backend
    // returns the full list (CT-e + MDF-e) — we slice client-side so a future filter
    // change on the server doesn't require a UI fix.
    cteEvents() {
      return this.events.filter(e => !CteService.isMdfeEvent(e.eventType));
    },
    mdfeEvents() {
      return this.events.filter(e => CteService.isMdfeEvent(e.eventType));
    },
  },
  watch: {
    accessKey: {
      immediate: true,
      handler(newKey) {
        if (newKey) this.fetch(newKey);
      },
    },
  },
  methods: {
    async fetch(accessKey) {
      this.loading = true;
      try {
        this.cte = await CteService.findByAccessKey(accessKey);
        if (this.cte) {
          this.loadEvents(accessKey);
        }
      } catch (e) {
        if (e.status === 404) {
          this.cte = null;
        } else {
          this.$message.error('Falha ao carregar CT-e.');
        }
      } finally {
        this.loading = false;
      }
    },
    async loadEvents(accessKey) {
      this.loadingEvents = true;
      try {
        this.events = await CteService.findEvents(accessKey);
      } catch (e) {
        this.$message.error('Falha ao carregar eventos do CT-e.');
      } finally {
        this.loadingEvents = false;
      }
    },
    formatDate(iso) {
      if (!iso) return '—';
      return moment(iso).format('DD/MM/YYYY HH:mm');
    },
    formatCurrency(value) {
      if (value == null) return '—';
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    },
    formatCnpj(cnpj) {
      if (!cnpj || cnpj.length !== 14) return cnpj || '';
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
    },
    describeEventType(type) {
      return CteService.describeEventType(type);
    },
    describeStatus(code) {
      return CteService.describeStatusCode(code);
    },
    goBack() {
      this.$router.push({ name: 'cte-list' });
    },
  },
};
</script>

<style lang="scss" scoped>
.cte-detail {
  h3 {
    margin-top: 24px;

    .event-count {
      margin-left: 8px;
      vertical-align: middle;
    }
  }

  .cancelled-tag {
    margin-top: 12px;
  }

  .access-key,
  .s3-path,
  .code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }

  .cnpj {
    color: #909399;
    font-size: 12px;
    display: block;
  }

  .reason {
    color: #909399;
    font-size: 11px;
    display: block;
  }

  .section-hint {
    color: #909399;
    display: block;
    margin: -8px 0 12px;
  }
}
</style>
